import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store/hooks';
import { hasPermission } from '@/lib/auth/permissions';
import { getRole, roleQueryKeys, updateRole } from '@/features/roles/api';
import type { UpdateRole } from '@/features/roles/role';
import { RoleForm, type RoleFormValues } from '@/features/roles/RoleForm';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from '@/lib/icons';
import { PageShell } from '@/components/layout/PageShell';
import { PageExpressiveLoading } from '@/components/feedback';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/_layout/roles/$roleId')({
  component: EditRolePage,
});

function EditRolePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { roleId } = useParams({ from: '/_layout/roles/$roleId' });
  const currentUser = useAppSelector((state) => state.auth.user);
  const canManage = hasPermission(currentUser, 'users', 'update');

  const { data, isLoading } = useQuery({
    queryKey: roleQueryKeys.detail(roleId),
    queryFn: () => getRole(roleId),
  });
  const role = data?.value;

  const mutation = useMutation({
    mutationFn: (payload: UpdateRole) => updateRole(roleId, payload),
    onSuccess: (result) => {
      if (result.succeeded && result.value) {
        queryClient.invalidateQueries({ queryKey: roleQueryKeys.lists() });
        queryClient.setQueryData(roleQueryKeys.detail(roleId), result);
        toast({
          title: t('common.success'),
          description: t('roles.update.success'),
        });
      } else {
        toast({
          title: t('common.error'),
          description: result.errors?.[0] ?? t('common.error'),
          variant: 'destructive',
        });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.error'),
        variant: 'destructive',
      });
    },
  });

  if (!canManage) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h1 className="text-2xl font-bold mb-4">{t('common.accessDenied')}</h1>
        <p className="text-muted-foreground">
          {t('common.noPermissionToView')}
        </p>
      </div>
    );
  }

  if (isLoading || !role) {
    return <PageExpressiveLoading text={t('common.loading.default')} />;
  }

  const onSubmit = async (values: RoleFormValues) => {
    await mutation.mutateAsync(values);
  };

  return (
    <PageShell
      title={role.name}
      description={role.description}
      actions={
        <Button variant="outline" onClick={() => navigate({ to: '/roles' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.actions.back')}
        </Button>
      }
    >
      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="flex gap-1 flex-wrap">
            {role.isSystem && (
              <Badge variant="secondary">{t('roles.flags.system')}</Badge>
            )}
            {role.isGeneric && (
              <Badge variant="outline">{t('roles.flags.generic')}</Badge>
            )}
            {role.bypassPermissions && (
              <Badge variant="default">{t('roles.flags.bypass')}</Badge>
            )}
          </div>

          <RoleForm
            initialValues={{
              name: role.name,
              description: role.description,
              permissions: role.permissions,
            }}
            submitLabel={t('common.actions.save')}
            submitting={mutation.isPending}
            disabled={role.isSystem}
            onCancel={() => navigate({ to: '/roles' })}
            onSubmit={onSubmit}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
}
